/**
 * 작성자 : 홍선영
 * 날짜 : 2023.09.04
 * 경로 : 안전 관리/게시판 - 현장전달사항
 */

import { useState, useRef, useEffect, RefObject } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { IModal } from 'customTypes';
import { COMCD_USE_YN, INIT_USE_YN_A } from '../../../_constants';
import { userState } from '../../../atoms';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import i18n from '../../../translation/i18n';
import { BtnDeep, BtnGhost, BtnRed } from '../../../components/Button';
import LoadButton from '../../../components/button/LoadButton';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import TuiGrid from '../../../components/Table/TuiGrid';
import BackButton from '../../../components/BackButton';
import IssueGuide from '../../../components/IssueGuide';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import { ynFilter } from '../../../utils/ynFilter';
import { logPost } from '../../../services/log';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import { useDebounce } from '../../../utils/useDebounce';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import BoardEditorInputForm from '../../../components/Form/BoardEditorInputForm';
import ReplyRow from '../../c_work/work4/ReplyRow';
import { useSetAuth } from '../../../utils/useSetAuth';
import { setupFlutterFileHandlers } from '../../../utils/flutterBridge';
import { isEditorContentEmpty } from '../../../utils/isEditorContentEmpty';
import { v4 } from 'uuid';
import { isMobile } from '../../../utils/checkMobile';
import useBackHistory from '../../../hooks/useBackHistory';

const Root = styled(ContentsContainerRoot)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: 767px) {
    flex-grow: 0;
    overflow: auto;

    > .inputFormsWrapper {
      padding: 0 !important;
    }
  }

  &.hideRoot {
    visibility: hidden;
    position: fixed;
    pointer-events: none;

    .tui-grid-body-area {
      overflow: hidden !important;
    }
  }

  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  > .inputFormsWrapper {
    height: 3.5rem !important;
    flex-shrink: 0;
    display: flex;
    flex-direction: row !important;
    align-items: center;
    padding: 0 1rem;
    border-bottom: 1px solid var(--outline);
  }
  .arrow-icon span {
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }
  .icon-btn {
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    gap: 0.25rem;
    cursor: pointer;
    border-radius: 5rem;
    padding-right: 1rem;
    padding-left: 0.5rem;
    font-size: 0.875rem;
    background-color: var(--tonal);
    color: var(--text-primary);
  }
  .icon-btn:hover {
    background-color: var(--tonal-deep);
  }

  // 파일첨부 버튼 너비 조절
  button.file-btn {
    width: 2rem;
    min-width: fit-content;
    height: fit-content;
    cursor: pointer;
    text-align: center;
    display: flex;
    justify-content: center;
    border-radius: 3px;
    font-size: 0.875rem;
    padding: 0 0.25rem;
  }

  .rangePickerWrapper {
    > div {
      left: 77%;
    }
  }
`;

const FooterButtons = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem;

  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }

  @media screen and (max-width: 767px) {
    padding: 0.5rem 0 0 0;
  }
`;

const NoticeTableWrapper = styled.div`
  flex-shrink: 0;
  border: 1px solid var(--outline);
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: var(--board);
  margin: 0.5rem 1rem 0.5rem 0.5rem;
  @media screen and (max-width: 767px) {
    margin: 0.5rem 0 0 0.5rem;
  }
`;

const NoticeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  tbody {
    tr {
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--tonal-light);
      }

      &.selected {
        background-color: var(--tonal-blue);
      }

      td {
        border-bottom: 1px solid var(--outline);
        color: var(--text-primary);

        &:first-child {
          text-align: center;
          width: 10%;
          min-width: 40px;
        }

        &:nth-child(2) {
          padding: 0.5rem;
          .title-content {
            display: flex;
            align-items: center;
            gap: 0.25rem;

            .attach-icon {
              font-size: 18px;
              color: var(--text-secondary);
            }

            .title-text {
              color: var(--text-tertiary);
            }

            .comment-count {
              color: var(--text-tertiary);
              font-size: 0.8125rem;
            }
          }
        }

        &:last-child {
          padding: 0.5rem 0.5rem;
          text-align: right;
          color: var(--text-secondary);
        }
      }
    }
  }
`;

// 제목 셀 커스텀 렌더러: 첨부파일 유무/댓글 개수 렌더링
class TitleWithMetaRenderer {
  el: HTMLDivElement;
  constructor(props: any) {
    const { grid, rowKey, value } = props;
    const row = grid.getRow(rowKey) || {};
    const title = value || '';

    const files = row.files || row.fileList || [];
    const fileCount = Array.isArray(files) ? files.length : Number(row.fileCount || row.fCount || 0);
    const hasFiles = Number(fileCount) > 0;

    const commentCount = Number(row.commentCount || 0);

    const root = document.createElement('div');
    root.style.display = 'flex';
    root.style.alignItems = 'center';
    root.style.gap = '0.25rem';

    if (hasFiles) {
      const attach = document.createElement('span');
      attach.className = 'material-symbols-rounded';
      attach.textContent = 'attach_file';
      attach.style.fontSize = '18px';
      attach.style.color = 'var(--text-secondary)';
      root.appendChild(attach);
    }

    const titleSpan = document.createElement('span');
    titleSpan.textContent = title;
    titleSpan.style.color = 'var(--text-tertiary)';
    titleSpan.style.textOverflow = 'ellipsis';
    titleSpan.style.overflow = 'hidden';
    titleSpan.style.whiteSpace = 'nowrap';
    root.appendChild(titleSpan);

    if (commentCount > 0) {
      const comment = document.createElement('span');
      comment.textContent = `[${commentCount}]`;
      comment.style.color = 'var(--text-tertiary)';
      comment.style.fontSize = '0.8125rem';
      root.appendChild(comment);
    }

    this.el = root;
  }
  getElement() {
    return this.el;
  }
  render(props: any) {}
}

interface Board {
  tIndex: string | number;
  title: string;
  contents: string;
  wDate: string;
  useYn: string;
  files?: any[];
  fileList?: any[];
  fileCount?: number;
  fCount?: number;
  commentCount?: number;
  rowKey?: number;
  noticeYn?: 'Y' | 'N';
}

const getInitialCommentsExpanded = () => !isMobile();

// type : 현장(s)/사내(c) 여부
const Board = ({ type = 'c' }: { type: 's' | 'c' }) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const { auth } = useSetAuth();
  const [columns, setColumns] = useState<any[]>([]);
  const size = useOutletContext<any>();
  const userInfoInputFormRef = useRef<any>(null); // 인풋 폼 ref (테이블 로우를 클릭했을 때 바로 inputForm 으로 스크롤 이동시키기 위한 ref)
  const [initBoardList, setInitBoardList] = useState<Board[]>([]);
  const [boardListForTui, setBoardListForTui] = useState<Board[] | null>(null);
  const [noticeListForTui, setNoticeListForTui] = useState<Board[]>([]);
  const [rowState, setRowState] = useState({
    title: '',
    contents: '',
    tIndex: 0,
    userId: '',
    noticeYn: 'N',
  }); // 서버에서 받아 온 raw data 어레이 중 1줄의 데이터 state
  const [originalRowState, setOriginalRowState] = useState({ title: '', contents: '', noticeYn: 'N' }); // 수정 모드일 때 원본 데이터 저장 (변경 여부 비교용)
  const [originalEditorData, setOriginalEditorData] = useState<string>(''); // 수정 모드일 때 원본 에디터 데이터 저장
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [searchTitle, setSearchTitle] = useState({ title: '' });
  const [userInputDateRange, setUserInputDateRange] = useState({ start: '', end: '' });
  const [useYn, setUseYn] = useState(INIT_USE_YN_A);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [isSaving, setIsSaving] = useState(false); // 저장 요청 진행 중 여부
  const [selectedRowKey, setSelectedRowKey] = useState('');
  const [selectedNoticeRowKey, setSelectedNoticeRowKey] = useState('');
  const [tIndex, setTIndex] = useState<null | number>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [pickerDateRange, setPickerDateRange] = useState([{ startDate: '', endDate: '', key: 'selection' }]);
  const [editorData, setEditorData] = useState<string>('');
  const [date, setDate] = useState({ start: '', end: '' });
  const [openSubPage, setOpenSubPage] = useState(false);
  const [method, setMethod] = useState<'get' | 'delete'>('get');
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const reqInfo = { path: '/freeboard', reqParam: { cCd: userInfo.hCd } };
  const debouncedTitle = useDebounce(searchTitle.title);
  const [comments, setComments] = useState<any[]>([]); // 댓글 목록 state
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(getInitialCommentsExpanded);
  const [filesState, setFilesState] = useState<{ fileId?: string; file?: File; fileName?: string; fUrl?: string; fileFormat?: string }[]>([]);
  const [deleteFileIndex, setDeleteFileIndex] = useState<string[]>([]);
  const isMobileView = isMobile();

  const backToMain = () => {
    initiateState();
    setSelectedRowKey('');
    setSelectedNoticeRowKey('');
    setOpenSubPage(false);
  };

  const { onBackPrev } = useBackHistory({ isOpen: openSubPage, onPrev: backToMain, pageName: 'board_detail' });

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: `자유게시판`,
      action: '조회',
      etc: ``,
    });
  }, []);

  // Flutter 모바일 파일 첨부 핸들러 설정
  useEffect(() => {
    const cleanup = setupFlutterFileHandlers(
      (fileData) => {
        // Flutter에서 파일이 선택되면 filesState에 추가
        const mapped = {
          fileId: v4(),
          file: fileData.file,
          fileName: fileData.name,
          fileFormat: fileData.name.slice(fileData.name.lastIndexOf('.') + 1),
        };
        setFilesState((prev) => [...prev, mapped]);
        toast.success(t('파일이 추가되었습니다'));
      },
      (error) => {
        console.error('Flutter 파일 선택 오류:', error);
        toast.error(t('파일 선택 중 오류가 발생했습니다'));
      },
      undefined,
      'all' // 모든 파일 타입 허용
    );

    return cleanup;
  }, [t]);

  useEffect(() => {
    setColumns([
      {
        header: t('제목'),
        name: 'title',
        align: 'left',
        sortable: true,
        minWidth: 200,
        renderer: { type: TitleWithMetaRenderer },
      },
      {
        header: t('작성자'),
        name: 'writer',
        align: 'center',
        filter: 'select',
        width: 100,
      },
      {
        header: t('작성일자'),
        name: 'wDate',
        align: 'center',
        sortable: true,
        width: 160,
        renderer: { classNames: ['text_secondary'] },
      },
    ]);
  }, [i18n.language]);

  useEffect(() => {
    if (size.innerSize.W > 1024) handlSearch();
  }, [userInputDateRange, useYn[COMCD_USE_YN], debouncedTitle, initBoardList]);

  useEffect(() => {
    if (pickerDateRange[0].startDate) {
      setUserInputDateRange((prev) => ({ ...prev, start: dayjs(pickerDateRange[0].startDate).format('YYYY-MM-DD') }));
    }
    if (pickerDateRange[0].endDate) {
      setUserInputDateRange((prev) => ({ ...prev, end: dayjs(pickerDateRange[0].endDate).format('YYYY-MM-DD') }));
    }
  }, [pickerDateRange[0].startDate, pickerDateRange[0].endDate]);

  const handlSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    // 검색 시 입력창 포커스(커서)를 해제해 가상 키보드를 내림
    // (커서가 남아 키패드가 안 내려가거나, 페이지 이동 시 키보드가 다시 올라오는 문제 방지)
    if (isMobileView) (document.activeElement as HTMLElement | null)?.blur?.();
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    if (initBoardList) applyFilter(initBoardList);
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handlSearch();
    }
  };

  const convertToDate = (dateString: string) => new Date(`${dateString}T00:00:00`);

  const applyFilter = (array: any[]) => {
    const titleFilteredArray = array.filter((item: any) => {
      return item.title?.toLowerCase()?.includes(searchTitle.title?.toLowerCase());
    });
    let newFilter = titleFilteredArray;
    if (userInputDateRange.end !== '') {
      newFilter = titleFilteredArray.filter((item) => {
        const itemDate = convertToDate(item.wDate.split(' ')[0]);
        const startDate = convertToDate(userInputDateRange.start);
        const endDate = convertToDate(userInputDateRange.end);
        if (userInputDateRange.start === '') return itemDate <= endDate;
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    const result = ynFilter(newFilter, 'useYn', useYn[COMCD_USE_YN]);
    if (result.length > 0) {
      const newArray = result.map((el: any, i: number) => ({ ...el, rowKey: i, sortKey: i }));
      setBoardListForTui(newArray.filter((el: any) => el.noticeYn === 'N'));
    } else {
      setBoardListForTui([]);
    }
  };

  const {
    data: boardList,
    isLoading: isLoadingBoardList,
    isFetching,
    isRefetching,
    isError,
    refetch: refetchBoardList,
  } = useQuery(['noticeList', userInfo.hCd, userInfo.sCd], () => fetchBoardList(method), {
    enabled: !!userInfo.hCd && !!userInfo.sCd,
  });

  const getParams = (methodParam: string) => {
    const commonReq = { ...reqInfo.reqParam };
    if (methodParam === 'get') return commonReq;
    return { ...commonReq, ...(type === 's' && { editor: userInfo.userId }) };
  };

  const fetchBoardList = async (methodParam: string) => {
    try {
      const req = getParams(methodParam);
      let res;
      let list = [];
      if (methodParam === 'get') {
        res = await apiGet({ path: reqInfo.path, req });
      }

      list = res.data.data;

      if (list) return { method, data: list };
      return { method };
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    // 조회/삭제 성공시
    if (boardList) {
      // 조회했을 때
      if (boardList.method === 'get') {
        setBoardListForTui(boardList.data.filter((el: any) => el.noticeYn === 'N'));
        setInitBoardList(boardList.data.filter((el: any) => el.noticeYn === 'N'));
        setNoticeListForTui(boardList.data.filter((el: any) => el.noticeYn === 'Y'));
        setRowState(boardList.data.find((el: any) => el.tIndex === rowState.tIndex || tIndex) || {});
      } else {
        // 삭제했을 때
        setMethod('get'); // 데이터 리패치

        setSelectedRowKey('');
        setSelectedNoticeRowKey('');
        initiateState();
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '자유게시판',
          action: '삭제',
          etc: `${rowState.tIndex}`,
        });
      }
    }
  }, [boardList]);

  const getReq = (reqData: any) => {
    const formData = new FormData();
    Object.keys(reqData).forEach((key) => {
      if (key === 'deleteFileIndex') {
        formData.append(key, JSON.stringify(reqData[key]));
      } else if (key === 'files') {
        reqData[key]
          .filter((file: any) => file.file)
          .forEach((file: any) => {
            formData.append('files', file.file);
          });
      } else if (reqData[key] !== undefined && reqData[key] !== null) {
        formData.append(key, reqData[key]);
      }
    });
    return formData;
  };

  const createNewNoticeAPI = async (reqData: any) => {
    const req = getReq(reqData);
    const res = await apiPost({ path: reqInfo.path, contentType: 'multipart/form-data', req });
    const { statusCode, data, message } = res.data;
    if (statusCode === 200) {
      const newData = { ...data, noticeYn: data.noticeYn || 'N' };
      setEditorData(data.contents);
      setIsNewAdd(false);
      setInitBoardList((prev) => [newData, ...prev]);
      setTIndex(data.tIndex);
      setRowState(newData);
      applyFilter([...initBoardList, newData]);

      if (data.noticeYn === 'Y') {
        setNoticeListForTui((prev) => [newData, ...prev]);
      }

      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '자유게시판',
        action: '신규등록',
        etc: `${data.tIndex}`,
      });
    }
  };

  const updateNoticeAPI = async (reqData: any) => {
    const req = getReq(reqData);
    const res = await apiPost({ path: reqInfo.path, contentType: 'multipart/form-data', req });
    const { statusCode, data, message } = res.data;

    if (statusCode === 200) {
      setEditorData(data.contents);
      setIsNewAdd(false);
      setTIndex(data.tIndex);
      setRowState(data);
      setOriginalEditorData(data.contents || '');
      setOriginalRowState({ title: data.title || '', contents: data.contents || '', noticeYn: data.noticeYn || 'N' });

      if (data.noticeYn === 'Y') {
        const findObj = initBoardList.find((el: any) => el.tIndex === data.tIndex);
        if (findObj) {
          if (findObj?.noticeYn === 'N') {
            // 일반글에서 공지글로 변경(기존 commentCount 유지)
            const updatedData = { ...data, commentCount: findObj.commentCount };
            setNoticeListForTui((prev) => [updatedData, ...prev]);
            setInitBoardList((prev) => prev.filter((el: any) => el.tIndex !== data.tIndex));
            // boardListForTui에서도 제거 (공지글로 변경되었으므로)
            setBoardListForTui((prev) => (prev ? prev.filter((el: any) => el.tIndex !== data.tIndex) : null));
          } else {
            // 공지글 수정: 기존 commentCount 유지
            setNoticeListForTui((prev) => prev.map((el: any) => (el.tIndex === data.tIndex ? { ...data, commentCount: el.commentCount } : el)));
          }
        } else {
          // noticeListForTui에만 있는 경우(기존 commentCount 유지)
          setNoticeListForTui((prev) => prev.map((el: any) => (el.tIndex === data.tIndex ? { ...data, wDate: el.wDate, writer: el.writer, commentCount: el.commentCount } : el)));
        }
      } else {
        const findObj = noticeListForTui.find((el: any) => el.tIndex === data.tIndex);
        if (findObj) {
          // 공지글에서 일반글로 변경(기존 commentCount 유지)
          const updatedData = { ...data, commentCount: findObj.commentCount };
          setNoticeListForTui((prev) => prev.filter((el: any) => el.tIndex !== data.tIndex));
          setInitBoardList((prev) => [updatedData, ...prev]);
          // boardListForTui에도 추가하고 필터 적용
          applyFilter([...initBoardList, updatedData]);
        } else {
          // 일반글 수정(기존 commentCount 유지)
          const updatedInitList = initBoardList.map((el: any) => (el.tIndex === data.tIndex ? { ...data, wDate: el.wDate, writer: el.writer, commentCount: el.commentCount } : el));
          setInitBoardList(updatedInitList);
          // boardListForTui도 업데이트
          setBoardListForTui((prev) => (prev ? prev.map((el: any) => (el.tIndex === data.tIndex ? { ...data, wDate: el.wDate, writer: el.writer, commentCount: el.commentCount } : el)) : null));
        }
      }

      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '자유게시판',
        action: '수정',
        etc: `${data.tIndex}`,
      });
    }
  };

  const onClickInitiateSearchOption = () => {
    setSearchTitle({ title: '' });
    setUserInputDateRange({ start: '', end: '' });
    setPickerDateRange([{ startDate: '', endDate: '', key: 'selection' }]);
    setUseYn(INIT_USE_YN_A);
    onClickNewRegistration(); // 우측 인풋창도 초기화
  };

  // tIndex 값 변경 시 댓글 목록 조회
  useEffect(() => {
    if (rowState.tIndex) getCommentsAPI();
  }, [rowState]);

  const getCommentsAPI = async () => {
    try {
      const req = { tIndex: Number(rowState.tIndex) };
      const res = await apiGet({ path: '/freeboard/comment', contentType: 'application/json', req });
      setComments(res.data.data || []);
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 댓글 수 업데이트 함수
  const updateCommentCount = (tIndexParam: number, count: number) => {
    setInitBoardList((prev) => prev.map((item) => (item.tIndex === tIndexParam ? { ...item, commentCount: count } : item)));
    setNoticeListForTui((prev) => prev.map((item) => (item.tIndex === tIndexParam ? { ...item, commentCount: count } : item)));

    // 현재 선택된 게시글의 rowState도 업데이트
    if (rowState.tIndex === tIndexParam) {
      setRowState((prev) => ({ ...prev, commentCount: count }));
    }
  };

  const onClickNoticeRow = (rowData: any) => {
    const noticeRow = noticeListForTui?.find((el: any) => el.tIndex === rowData.tIndex) || ({} as any);
    setIsNewAdd(false);
    setRowState(noticeRow);
    setEditorData(noticeRow.contents || '');
    // 원본 데이터 저장 (변경 여부 비교용)
    setOriginalRowState({
      title: noticeRow.title || '',
      contents: noticeRow.contents || '',
      noticeYn: noticeRow.noticeYn || 'N',
    });
    setOriginalEditorData(noticeRow.contents || '');
    const mapped = (rowData.files || []).map((el: any) => {
      const splitUrl = (el.fUrl || '').split('/');
      const fileName = splitUrl[splitUrl.length - 1];
      const fileFormat = el.fUrl.slice(el.fUrl.lastIndexOf('.') + 1);
      return { fileId: String(el.fIndex) || (v4() as string), file: el.file, fileName, fileFormat, fUrl: el.fUrl };
    });
    setFilesState(mapped);
    setDeleteFileIndex([]);
    setSelectedNoticeRowKey(rowData.rowKey);
    setSelectedRowKey('');

    if (size.innerSize.W <= 1024) {
      setOpenSubPage(true);
    }
  };

  // 사용자 Row 클릭
  const onClickRow = async (rowData: any) => {
    setIsCommentsExpanded(false);

    if (rowData.tIndex) {
      setDate({ start: rowData?.cDate || '', end: rowData?.fDate || '' });
      setTIndex(rowData.tIndex?.toString());
      setSelectedRowKey(rowData.rowKey?.toString());

      if (type === 'c') {
        setRowState(rowData);
        setEditorData(rowData.contents);
        // 원본 데이터 저장 (변경 여부 비교용)
        setOriginalRowState({ title: rowData.title || '', contents: rowData.contents || '', noticeYn: rowData.noticeYn || 'N' });
        setOriginalEditorData(rowData.contents || '');
        setIsNewAdd(false);
      }
      const mapped = (rowData.files || []).map((el: any) => {
        const splitUrl = (el.fUrl || '').split('/');
        const fileName = splitUrl[splitUrl.length - 1];
        const fileFormat = el.fUrl.slice(el.fUrl.lastIndexOf('.') + 1);
        return { fileId: String(el.fIndex) || (v4() as string), file: el.file, fileName, fileFormat, fUrl: el.fUrl };
      });
      setFilesState(mapped);
      setDeleteFileIndex([]);
    }
    setSelectedNoticeRowKey('');
    if (size.innerSize.W <= 1024) {
      setOpenSubPage(true);
    }
  };

  // 인풋폼 초기화
  const initiateState = () => {
    Object.keys(rowState).map((el: any) => {
      // 기본값 제외하고 초기화
      return setRowState((prev: any) => ({
        ...prev,
        [el]: '',
        useYn: 'Y',
        writer: userInfo.userId,
        editor: userInfo.userId,
        noticeYn: 'N',
      }));
    });
    setIsNewAdd(true);
    setIsSaveClicked(false);
    setDate({ start: '', end: '' });
    setEditorData('');
    // 원본 데이터 초기화
    setOriginalRowState({ title: '', contents: '', noticeYn: 'N' });
    setOriginalEditorData('');
    setTIndex(null);
    setFilesState([]);
    setDeleteFileIndex([]);
  };

  const onClickNewRegistration = () => {
    initiateState();
    setSelectedRowKey('');
    setSelectedNoticeRowKey('');
    setOpenSubPage(true);
  };

  const onClickSave = async () => {
    // 저장 요청 진행 중이면 저장 불가
    if (isSaving) {
      return;
    }

    // 저장 시작
    setIsSaving(true);

    try {
      // 수정 모드일 때 원본 데이터와 비교하여 변경된 내용이 없으면 저장 처리하지 않음
      if (!isNewAdd) {
        const hasChanges =
          rowState.title !== originalRowState.title ||
          rowState.noticeYn !== originalRowState.noticeYn ||
          editorData !== originalEditorData ||
          filesState.some((file) => file.file) || // 새로 추가된 파일이 있는지
          deleteFileIndex.length > 0; // 삭제된 파일이 있는지

        if (!hasChanges) {
          toast.warning(t('변경된 내용이 없습니다'));
          setIsSaving(false);
          return;
        }
      }
      setIsSaveClicked(true);
      const { title } = rowState;
      if (title?.trim() === '') {
        toast.warning(t('제목을 입력하세요'));
        setIsSaving(false);
        return;
      }
      if (isEditorContentEmpty(editorData)) {
        toast.warning(t('내용을 입력하세요'));
        setIsSaving(false);
        return;
      }

      const payload = {
        cCd: userInfo.hCd,
        tIndex: isNewAdd ? 0 : Number(rowState.tIndex) || 0,
        title,
        contents: editorData,
        delYn: 'N',
        files: filesState.filter((el) => el.file),
        noticeYn: rowState.noticeYn,
      };

      if (isNewAdd) {
        await createNewNoticeAPI(payload);
      } else {
        await updateNoticeAPI(payload);
      }
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    refetchBoardList();
  }, [method]);

  const deleteAPI = async () => {
    try {
      const req = { tIndex: Number(rowState.tIndex) };
      const res = await apiDelete({ path: reqInfo.path, contentType: 'application/json', req });
      if (res.data.statusCode === 200) {
        setInitBoardList((prev) => prev.filter((el: any) => el.tIndex !== rowState.tIndex));
        if (rowState.noticeYn === 'Y') {
          setNoticeListForTui((prev) => prev.filter((el: any) => el.tIndex !== rowState.tIndex));
        }

        toast.success(t(res.data.message));
        onClickNewRegistration(); // 모두 초기화
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '자유게시판',
          action: '삭제',
          etc: `제목 : ${rowState.title}`,
        });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickDelete = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const hasDataForTab = boardList && !!boardList.data;
  const isLoading = !columns || isFetching || isLoadingBoardList || boardListForTui === null;
  const shouldRenderData = !isLoading && !!hasDataForTab && !!boardListForTui;

  if (isError) return <IssueGuide />;

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root loading={!shouldRenderData} className={size.innerSize.W > 1024 || (size.innerSize.W <= 1024 && !openSubPage) ? 'showRoot' : 'hideRoot'}>
        <SearchOptions align='left'>
          {size.innerSize.W > 1024 ? (
            <>
              <div className='inputsWrapper'>
                <div className='searchResult'>
                  {t('총')}
                  <span>{boardListForTui?.length}</span>
                  {t('개')}
                </div>
                <div className='inputForm-row-fit'>
                  <span className='calendarLabel'>{t('작성일자')}</span>
                  <div className='inputForm-col'>
                    <div className='flex-basic'>
                      <input id='startInput' type='text' value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setPickerDateRange, setUserInputDateRange)} />
                      <span className='wave'> ~ </span>
                      <input id='endInput' type='text' value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setPickerDateRange, setUserInputDateRange)} />
                    </div>
                    {visibleRangePicker && (
                      <div className='rangePickerWrapper'>
                        <RangePicker state={pickerDateRange} setState={setPickerDateRange} setVisible={setVisibleRangePicker} />
                      </div>
                    )}
                  </div>
                  <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                    <span className='material-symbols-rounded'>calendar_month</span>
                  </button>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <Input label='' placeholder={t('제목')} type='text' id='title' name='title' state={searchTitle} setState={setSearchTitle} maxLength={100} />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  </div>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickNewRegistration}>
                      <span className='material-symbols-rounded'>add</span>
                      {t('등록')}
                    </BtnGhost>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                  <div className='inputForm-col'>
                    <Input label='' placeholder={t('제목')} type='search' id='title' name='title' state={searchTitle} setState={setSearchTitle} onKeyDown={onEnterKeyDown} ico='search' />
                  </div>
                </div>
                <BtnGhost onClick={onClickFilter} className='filter-btn'>
                  <span className='material-symbols-rounded'>filter_alt</span>
                </BtnGhost>
              </div>
              <div className='inputsWrapper'>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={onClickNewRegistration}>
                    <span className='material-symbols-rounded'>add</span>
                    {t('등록')}
                  </BtnGhost>
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        {isLoading ? (
          <LoadingModalBackground>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
          </LoadingModalBackground>
        ) : (
          <>
            {noticeListForTui && noticeListForTui.length > 0 && (
              <NoticeTableWrapper>
                <NoticeTable>
                  <tbody>
                    {noticeListForTui.map((item: Board, index: number) => {
                      const files = item.files || item.fileList || [];
                      const fileCount = Array.isArray(files) ? files.length : Number(item.fileCount || item.fCount || 0);
                      const hasFiles = Number(fileCount) > 0;
                      const commentCount = Number(item.commentCount || 0);
                      const rowKey = index;
                      const isSelected = selectedNoticeRowKey !== '' && String(rowKey) === String(selectedNoticeRowKey);

                      return (
                        <tr key={item.tIndex} className={isSelected ? 'selected' : ''} onClick={() => onClickNoticeRow({ ...item, rowKey })}>
                          <td>[{t('공지')}]</td>
                          <td>
                            <div className='title-content'>
                              {hasFiles && <span className='material-symbols-rounded attach-icon'>attach_file</span>}
                              <span className='title-text'>{item.title || ''}</span>
                              {commentCount > 0 && <span className='comment-count'>[{commentCount}]</span>}
                            </div>
                          </td>
                          <td>{item.wDate || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </NoticeTable>
              </NoticeTableWrapper>
            )}
            <TuiGridWrapper componentRef={componentRef}>
              <TuiGrid
                data={boardListForTui}
                columns={columns}
                onClickRow={onClickRow}
                perPage={15}
                rowKey={selectedRowKey}
                scrollX
                height={tuiHeight}
                frozenCount={size.innerSize.W <= 1024 ? 0 : 1}
                usePagenation
                idxKey='tIndex'
              />
            </TuiGridWrapper>
          </>
        )}
      </Root>
      {(size.innerSize.W > 1024 || (size.innerSize.W <= 1024 && openSubPage)) && (
        <Root loading={false}>
          <div className='inputFormsWrapper'>
            {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
            <div className='formTitle'>{isNewAdd ? t(`새 게시글`) : t(`게시글 상세`)}</div>
          </div>
          <BoardEditorInputForm
            state={rowState}
            setState={setRowState}
            isNewAdd={isNewAdd}
            userInfoInputFormRef={userInfoInputFormRef}
            isSaveClicked={isSaveClicked}
            editorData={editorData}
            setEditorData={setEditorData}
            filesState={filesState}
            setFilesState={setFilesState}
            deleteFileIndex={deleteFileIndex}
            setDeleteFileIndex={setDeleteFileIndex}
            setOpenModal={setOpenModal}
          />
          {/* 댓글 컴포넌트 - 신규 등록이 아닐 때만 표시 */}
          {/* {!isNewAdd && rowState.cSeq && <ReplyRow rowState={rowState as any} data={comments} />} */}
          {!isNewAdd && (
            <ReplyRow
              rowState={rowState as any}
              data={comments}
              endPoint='/freeboard/comment'
              updateCommentCount={updateCommentCount}
              isCommentsExpanded={isCommentsExpanded}
              setIsCommentsExpanded={setIsCommentsExpanded}
            />
          )}
          {/** 새 글이 아니면서, 본인이 작성자일 때에만 삭제/저장 버튼 표시 */}
          <FooterButtons>
            {!isNewAdd && auth.deleteAuth && (rowState.userId === userInfo.userId || userInfo.gCd === 'MM') && <BtnRed onClick={onClickDelete}>{t('삭제')}</BtnRed>}
            {((isNewAdd && (auth.createAuth || auth.updateAuth)) || (!isNewAdd && (auth.createAuth || auth.updateAuth) && (rowState.userId === userInfo.userId || userInfo.gCd === 'MM'))) && (
              <LoadButton text={t('저장')} color='blue' isLoading={isSaving} onClick={onClickSave} />
            )}
          </FooterButtons>
        </Root>
      )}
      <Portal openModal={openModal?.status}>{openModal && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handlSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{type === 's' ? t('게시 기간') : t('적용일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setPickerDateRange, setUserInputDateRange)} />
                    <span className='wave'> ~ </span>
                    <input id='endInput' type='text' readOnly value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setPickerDateRange, setUserInputDateRange)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={pickerDateRange} setState={setPickerDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Board;
